import { useState, useEffect, useRef } from "react";
import { todayISO } from "@/lib/utils";
import PageMeta from "@/components/common/PageMeta";
import PageBreadcrumb from "@/components/common/PageBreadCrumb";
import Label from "@/components/form/Label";
import Input from "@/components/form/input/InputField";
import {
  eggStockReportService,
  type EggStockReport,
  type EggStockRow,
} from "@/services/eggStockReportService";
import { handleApiError } from "@/services/api";

type ColumnDef = {
  key: string;
  label: string;
  shortLabel: string;
  align: "left" | "right";
  render: (row: EggStockRow) => string;
  colorClass?: string;
  defaultVisible: boolean;
};

const fmt = (v: number) => (v === 0 ? "-" : v.toLocaleString());
const fmtDec = (v: number) => (v === 0 ? "-" : v.toFixed(2));
const fmtMoney = (v: number) =>
  v === 0 ? "-" : v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// A stock below zero is not a stock: more eggs were sold than were ever
// recorded as laid, which means production entries are missing (on production
// one farm logged eggs on a single day while selling daily, and this page read
// "Current Stock: -285000.00 trays"). Shown as unknown rather than as a figure.
const isStockUnknown = (v: number) => v < 0;
const fmtStock = (v: number) => (isStockUnknown(v) ? "Unknown" : fmtDec(v));
const fmtDate = (d: string) => {
  const dt = new Date(d);
  return `${dt.getDate()}/${dt.getMonth() + 1}`;
};

const columns: ColumnDef[] = [
  { key: "date", label: "Date", shortLabel: "Date", align: "left", render: (r) => fmtDate(r.date), defaultVisible: true },
  { key: "production_trays", label: "Production (Trays)", shortLabel: "Prod Trays", align: "right", render: (r) => fmtDec(r.production_trays), colorClass: "text-success-700 dark:text-success-400 font-medium", defaultVisible: true },
  { key: "production_eggs", label: "Production (Eggs)", shortLabel: "Prod Eggs", align: "right", render: (r) => fmt(r.production_eggs), defaultVisible: false },
  // Sales is GOLD, not brand: this is one of a coded triple (production /
  // sales / breakage), and brand-700 sits at 1.05:1 against the body text now
  // that both are dark greens. Hue is what separates a coded column, not
  // luminance — green, gold and red are three things; three dark values are one.
  { key: "sales_trays", label: "Sales (Trays)", shortLabel: "Sale Trays", align: "right", render: (r) => fmtDec(r.sales_trays), colorClass: "text-accent-700 dark:text-accent-400 font-medium", defaultVisible: true },
  { key: "sales_eggs", label: "Sales (Eggs)", shortLabel: "Sale Eggs", align: "right", render: (r) => fmt(r.sales_eggs), defaultVisible: false },
  { key: "avg_price_per_egg", label: "Avg Price/Egg", shortLabel: "Rate/Egg", align: "right", render: (r) => fmtDec(r.avg_price_per_egg), defaultVisible: true },
  { key: "sale_amount", label: "Sale Amount", shortLabel: "Amount", align: "right", render: (r) => fmtMoney(r.sale_amount), defaultVisible: true },
  { key: "broken_eggs", label: "Breakage (Eggs)", shortLabel: "Broken", align: "right", render: (r) => fmt(r.broken_eggs), colorClass: "text-error-600 dark:text-error-400", defaultVisible: true },
  { key: "cum_production_trays", label: "Cum. Production", shortLabel: "Cum Prod", align: "right", render: (r) => fmtDec(r.cum_production_trays), defaultVisible: true },
  { key: "cum_sales_trays", label: "Cum. Sales", shortLabel: "Cum Sales", align: "right", render: (r) => fmtDec(r.cum_sales_trays), defaultVisible: true },
  { key: "stock_trays", label: "Stock (Trays)", shortLabel: "Stock", align: "right", render: (r) => fmtStock(r.stock_trays), colorClass: "font-semibold", defaultVisible: true },
];

const STORAGE_KEY = "egg-stock-report-columns";

function loadVisibleColumns(): Set<string> {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) return new Set(JSON.parse(saved));
  } catch { /* ignore */ }
  return new Set(columns.filter((c) => c.defaultVisible).map((c) => c.key));
}

export default function EggStockReportPage() {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState(todayISO());
  const [report, setReport] = useState<EggStockReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [visibleCols, setVisibleCols] = useState<Set<string>>(loadVisibleColumns);
  const [colPickerOpen, setColPickerOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLoading(true);
    eggStockReportService
      .getReport(from || undefined, to || undefined)
      .then(setReport)
      .catch(handleApiError)
      .finally(() => setLoading(false));
  }, [from, to]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setColPickerOpen(false);
      }
    };
    if (colPickerOpen) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [colPickerOpen]);

  const toggleColumn = (key: string) => {
    setVisibleCols((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...next]));
      return next;
    });
  };

  const activeCols = columns.filter((c) => visibleCols.has(c.key));

  // Summary row
  const totals = report?.rows.reduce(
    (acc, row) => ({
      productionTrays: acc.productionTrays + row.production_trays,
      salesTrays: acc.salesTrays + row.sales_trays,
      saleAmount: acc.saleAmount + row.sale_amount,
      brokenEggs: acc.brokenEggs + row.broken_eggs,
    }),
    { productionTrays: 0, salesTrays: 0, saleAmount: 0, brokenEggs: 0 }
  );

  const selectClass =
    "h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90";

  return (
    <>
      <PageMeta title="Egg Stock Report | Poultry Book" description="Daily egg stock balance report" />
      <PageBreadcrumb pageTitle="Egg Stock Report" />

      <div className="card">
        {/* Filters */}
        <div className="border-b border-gray-200 p-4 dark:border-gray-700">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <Label>From</Label>
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div>
              <Label>To</Label>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
            <div className="relative" ref={pickerRef}>
              <Label>Columns</Label>
              <button
                type="button"
                onClick={() => setColPickerOpen((v) => !v)}
                className={selectClass + " flex items-center justify-between cursor-pointer"}
              >
                <span>{activeCols.length} of {columns.length} shown</span>
                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {colPickerOpen && (
                <div className="absolute right-0 z-50 mt-1 w-64 max-h-80 overflow-y-auto rounded-lg border border-gray-200 bg-white p-2 shadow-lg dark:border-gray-700 dark:bg-gray-900">
                  <div className="mb-2 flex gap-2 border-b border-gray-100 pb-2 dark:border-gray-700">
                    <button
                      type="button"
                      className="text-xs text-brand-500 hover:underline"
                      onClick={() => {
                        const all = new Set(columns.map((c) => c.key));
                        setVisibleCols(all);
                        localStorage.setItem(STORAGE_KEY, JSON.stringify([...all]));
                      }}
                    >
                      Show All
                    </button>
                    <button
                      type="button"
                      className="text-xs text-brand-500 hover:underline"
                      onClick={() => {
                        const defaults = new Set(columns.filter((c) => c.defaultVisible).map((c) => c.key));
                        setVisibleCols(defaults);
                        localStorage.setItem(STORAGE_KEY, JSON.stringify([...defaults]));
                      }}
                    >
                      Reset
                    </button>
                  </div>
                  {columns.map((col) => (
                    <label
                      key={col.key}
                      className="flex items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-gray-50 cursor-pointer dark:hover:bg-gray-800"
                    >
                      <input
                        type="checkbox"
                        checked={visibleCols.has(col.key)}
                        onChange={() => toggleColumn(col.key)}
                        className="rounded border-gray-300 text-brand-500 focus:ring-brand-500"
                      />
                      {col.label}
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          {totals && report && report.rows.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-4 text-sm text-gray-600 dark:text-gray-400">
              <span><strong>Period Total Production:</strong> {totals.productionTrays.toFixed(2)} trays</span>
              <span><strong>Period Total Sales:</strong> {totals.salesTrays.toFixed(2)} trays</span>
              <span><strong>Period Revenue:</strong> {fmtMoney(totals.saleAmount)}</span>
              <span>
                <strong>Current Stock:</strong>{" "}
                {isStockUnknown(report.rows[0].stock_trays) ? "unknown" : `${report.rows[0].stock_trays.toFixed(2)} trays`}
              </span>
            </div>
          )}
          {report && report.rows.some((r) => isStockUnknown(r.stock_trays)) && (
            <p role="status" className="mt-2 text-sm text-warning-700 dark:text-warning-400">
              {isStockUnknown(report.rows[0].stock_trays)
                ? "More eggs have been sold than were recorded as laid, so the stock cannot be worked out."
                : "On some earlier days more eggs had been sold than recorded as laid, so their stock shows as unknown."}{" "}
              Egg production is missing for some days — record it in the Daily Report.
            </p>
          )}
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading report...</div>
          ) : !report || report.rows.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No data for the selected period.</div>
          ) : (
            <table className="w-max min-w-full">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50 dark:border-gray-700 dark:bg-gray-800">
                  {activeCols.map((col) => (
                    <th
                      key={col.key}
                      className={`whitespace-nowrap px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 ${
                        col.align === "right" ? "text-right" : "text-left"
                      }`}
                    >
                      {col.shortLabel}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {report.rows.map((row, i) => (
                  <tr
                    key={row.date}
                    className={`border-b border-gray-50 dark:border-gray-800 ${
                      i % 2 === 0 ? "" : "bg-gray-50/50 dark:bg-gray-800/30"
                    }`}
                  >
                    {activeCols.map((col) => (
                      <td
                        key={col.key}
                        className={`whitespace-nowrap px-4 py-2 text-sm ${
                          col.align === "right" ? "text-right" : "text-left"
                        } ${col.colorClass ?? "text-gray-700 dark:text-gray-300"}`}
                      >
                        {col.render(row)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}
